import {
  Dropdown,
  DropdownSubmenu,
  IconButton,
  openDialog,
  Tooltip,
  useTranslationContext,
} from 'platform/components';

import {useMemo} from 'react';

import {isNotNil, noop} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

import {Preset} from '../../types/Api';
import {EditPresetDialog} from './components/EditPresetDialog/EditPresetDialog';
import {PresetButton} from './components/PresetButton/PresetButton';
import {PresetButtonActions} from './components/PresetButtonActions/PresetButtonActions';
import {PresetApiStateEnum} from './constants/PresetApiStateEnum';
import {usePresetApiState} from './hooks/usePresetApiState';
import {usePresetOverflow} from './hooks/usePresetOverflow';
import {usePresetRowCounts} from './hooks/usePresetRowCounts';
import {PresetsPanelRendererProps} from './types/PresetsPanelRendererType';

const PRESET_WRAPPER_HEIGHT = 36;

export function PresetsPanelRenderer(props: PresetsPanelRendererProps) {
  const t = useTranslationContext();

  // Ghost div always receives the original server order so that visibleCount
  // is computed from a stable, unchanged layout (no layout shift side-effects).
  const {visibleCount, wrapperWidth, buttonsWrapperRef, buttonsGhostRef} = usePresetOverflow({
    presets: props.presets,
  });

  const {
    apiState,
    isPresetChangeDisabled,
    disabledPresetChange,
    allowPresetChange,
    updatePresetProxy,
    duplicatePresetProxy,
    deletePresetProxy,
    updatePresetPositionProxy,
  } = usePresetApiState({
    updatePreset: props.updatePreset,
    duplicatePreset: props.duplicatePreset,
    deletePreset: props.deletePreset,
    updatePresetPosition: props.updatePresetPosition,
  });

  const activePresetId = (props.activePreset as Preset)?.id;

  // If the active preset falls inside the hidden (overflow) slice, promote it
  // to position 0 of the visible slice so the user always sees it in the toolbar.
  // Reorder is applied AFTER visibleCount is known so the ghost-div measurement
  // is never affected — avoiding any circular layout recalculation.
  // Stable sort: relative order of all other items is preserved.
  /**
   * @mandatory Complex conditional computation that must remain reference-stable.
   * visibleItems is a dep of getPresetsCount useCallback (below) and triggers
   * the row count fetch effect. Unstable identity would cause infinite fetch loops.
   */
  const {visibleItems, hiddenItems} = useMemo<{
    visibleItems: Preset[] | undefined;
    hiddenItems: Preset[] | undefined;
  }>(() => {
    if (!props.presets || visibleCount === undefined) {
      return {visibleItems: props.presets?.slice(0, visibleCount), hiddenItems: undefined};
    }

    const activeIndex = props.presets.findIndex((p) => p.id === activePresetId);
    const activeIsHidden = activeIndex >= visibleCount;

    if (!activeIsHidden) {
      // Active preset is already visible — no reorder needed, return slices of
      // the original array (same references, no new allocations).
      return {
        visibleItems: props.presets.slice(0, visibleCount),
        hiddenItems: props.presets.slice(visibleCount),
      };
    }

    // Active preset is hidden: promote it to position 0.
    // visibleItems = [active, ...original visible[0..visibleCount-2]]
    // hiddenItems  = [original visible[visibleCount-1], ...original hidden (minus active)]
    const active = props.presets[activeIndex];
    const originalVisible = props.presets.slice(0, visibleCount);
    const originalHidden = props.presets.slice(visibleCount).filter((p) => p.id !== activePresetId);

    return {
      visibleItems: [active, ...originalVisible.slice(0, visibleCount - 1)],
      hiddenItems: [originalVisible[visibleCount - 1], ...originalHidden],
    };
  }, [props.presets, visibleCount, activePresetId]);

  const rowCounts = usePresetRowCounts({
    visibleItems,
    rowCountGetter: props.rowCountGetter,
  });
  const presetsRowCount = rowCounts.presetsRowCount;

  return (
    <div className="relative gap-2 flex-grow z-(--z-content) overflow-hidden inline-flex items-center">
      <Tooltip label={t('page.actions.createPreset')} placement="top">
        <div className="rounded-full overflow-hidden bg-gray-100">
          <IconButton
            onClick={() =>
              openDialog(
                <EditPresetDialog
                  onSubmit={props.createPreset}
                  data-testid={suffixTestId('createPreset', props)}
                />,
                {
                  id: 'createPreset',
                  title: t('general.presets.createPreset'),
                  'data-testid': suffixTestId('createPreset', props),
                  size: 'small',
                  scrollBehavior: 'outside',
                }
              )
            }
            data-testid={suffixTestId('createPresetButton', props)}
            icon="content/add"
          />
        </div>
      </Tooltip>

      <div
        ref={buttonsWrapperRef}
        style={{maxHeight: PRESET_WRAPPER_HEIGHT}}
        className="flex flex-nowrap overflow-hidden w-full whitespace-nowrap flex-shrink gap-x-2"
      >
        {visibleItems?.map((preset) => {
          const isActive = preset.id === (props.activePreset as Preset).id;
          const state = isNotNil(apiState[preset.id])
            ? apiState[preset.id]
            : PresetApiStateEnum.idle;

          const globalIndex = props.presets?.findIndex((p) => p.id === preset.id) ?? -1;

          return (
            <PresetButton
              rowCount={presetsRowCount[preset.dataQueryId]}
              unsaved={props.isPresetChanged && isActive}
              key={preset.id}
              preset={preset}
              state={state}
              active={isActive}
              isDisabled={isPresetChangeDisabled}
              onClick={() => {
                disabledPresetChange();
                const operation = isActive
                  ? props.deactivatePreset()
                  : props.activatePreset(preset);
                operation.finally(allowPresetChange);
              }}
              data-testid={suffixTestId(`preset-${preset.title}`, props)}
              actions={
                <PresetButtonActions
                  preset={preset}
                  index={globalIndex}
                  presetChanged={props.isPresetChanged}
                  activePresetId={(props.activePreset as Preset)?.id}
                  updatePreset={updatePresetProxy}
                  deletePreset={deletePresetProxy}
                  duplicatePreset={duplicatePresetProxy}
                  resetOverrides={props.resetOverrides}
                  updatePresetPosition={updatePresetPositionProxy}
                  data-testid={suffixTestId(`preset-${preset.title}`, props)}
                  openEditModal={() =>
                    openDialog(
                      <EditPresetDialog
                        presetToEdit={preset}
                        data-testid={suffixTestId('editPreset', props)}
                        onSubmit={(title, description) =>
                          props.renamePreset(preset.id, title, description)
                        }
                      />,
                      {
                        id: 'editPreset',
                        title: t('general.presets.editPreset', {presetName: preset.title}),
                        'data-testid': suffixTestId('editPreset', props),
                        size: 'small',
                        scrollBehavior: 'outside',
                      }
                    )
                  }
                  presetsLength={props.presets?.length ?? 0}
                />
              }
            />
          );
        })}
        <div className="inline-flex">
          {isNotNil(hiddenItems?.length) && (hiddenItems?.length ?? 0) > 0 && (
            <Dropdown
              isLazy
              strategy="fixed"
              dropdownControl={
                <IconButton icon="navigation/more_vert" data-testid="button-preset.more" />
              }
              data-testid={suffixTestId('dropDownPresetMore', props)}
            >
              {hiddenItems?.map((preset) => {
                const globalIndex = props.presets?.findIndex((p) => p.id === preset.id) ?? -1;
                const isActive = preset.id === (props.activePreset as Preset).id;
                return (
                  <DropdownSubmenu
                    isLazy
                    isActive={isActive}
                    key={preset.id}
                    onClick={() => {
                      if (isActive) {
                        props.deactivatePreset();
                      } else {
                        props.activatePreset(preset);
                      }
                    }}
                    label={preset.title}
                    data-id={`menu-preset.${preset.title}`}
                  >
                    <PresetButtonActions
                      preset={preset}
                      index={globalIndex}
                      presetChanged={props.isPresetChanged}
                      activePresetId={(props.activePreset as Preset)?.id}
                      updatePreset={updatePresetProxy}
                      deletePreset={deletePresetProxy}
                      duplicatePreset={duplicatePresetProxy}
                      resetOverrides={props.resetOverrides}
                      updatePresetPosition={updatePresetPositionProxy}
                      data-testid={`menu-preset.${preset.title}`}
                      openEditModal={() =>
                        openDialog(
                          <EditPresetDialog
                            presetToEdit={preset}
                            data-testid="button-createPreset"
                            onSubmit={(title, description) =>
                              props.renamePreset(preset.id, title, description)
                            }
                          />,
                          {
                            title: t('general.presets.editPreset', {presetName: preset.title}),
                            'data-testid': 'editPreset',
                            size: 'small',
                            scrollBehavior: 'outside',
                          }
                        )
                      }
                      presetsLength={props.presets?.length ?? 0}
                    />
                  </DropdownSubmenu>
                );
              })}
            </Dropdown>
          )}
        </div>
      </div>

      {/* Ghost wrapper for measuring overflow */}
      <div
        ref={buttonsGhostRef}
        className="pointer-events-none flex flex-wrap overflow-hidden w-full whitespace-nowrap flex-shrink gap-x-2"
        style={{
          position: 'absolute',
          // Intentionally large negative value — this ghost div is only used
          // to measure button overflow and must never be visible to users.
          // Not a candidate for the EAG_Z scale; see constants/zIndex.ts.
          zIndex: -999,
          opacity: 0,
          visibility: 'hidden',
          width: wrapperWidth,
        }}
      >
        {props.presets?.map((preset) => (
          <PresetButton
            rowCount={presetsRowCount[preset.dataQueryId]}
            unsaved={false}
            key={`ghost-${preset.id}`}
            preset={preset}
            state={PresetApiStateEnum.idle}
            active={false}
            onClick={noop}
            data-id={`menu-preset.${preset.title}`}
          />
        ))}
      </div>
    </div>
  );
}
